import React, { useState, useEffect } from 'react';
import { User, Lock, Shield, Save, Eye, EyeOff } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const Field = ({ label, type = 'text', value, onChange, testid, placeholder, autoComplete = 'off' }) => {
  const [show, setShow] = useState(false);
  const isPassword = type === 'password';
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">{label}</label>
      <div className="relative">
        <input
          data-testid={testid}
          type={isPassword && show ? 'text' : type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-white/40 transition-colors"
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow(s => !s)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white p-1"
            tabIndex={-1}
          >
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
};

const Section = ({ icon: Icon, title, description, children, testid }) => (
  <div data-testid={testid} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-5 sm:p-6">
    <div className="flex items-start gap-3 mb-5">
      <div className="p-2 bg-white/5 rounded-md">
        <Icon className="w-4 h-4 text-white/70" />
      </div>
      <div className="flex-1">
        <h3 className="text-sm uppercase tracking-wider text-white">{title}</h3>
        {description && <p className="text-xs text-white/50 mt-1">{description}</p>}
      </div>
    </div>
    {children}
  </div>
);

const AccountPanel = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Cambio de usuario/contraseña
  const [currentPw1, setCurrentPw1] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // Pregunta de seguridad
  const [currentPw2, setCurrentPw2] = useState('');
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [savingSecurity, setSavingSecurity] = useState(false);

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/auth/profile', { headers: { Authorization: `Bearer ${token}` } });
      setProfile(res.data);
      setNewUsername(res.data.username || '');
      setSecurityQuestion(res.data.security_question || '');
    } catch (e) {
      toast.error('No se pudo cargar el perfil');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!currentPw1) { toast.error('Ingresa tu contraseña actual'); return; }
    if (newPassword && newPassword !== confirmPassword) { toast.error('Las contraseñas nuevas no coinciden'); return; }
    if (newPassword && newPassword.length < 6) { toast.error('La nueva contraseña debe tener al menos 6 caracteres'); return; }
    const usernameChanged = newUsername && newUsername !== profile.username;
    if (!usernameChanged && !newPassword) { toast.error('No hay cambios para guardar'); return; }

    setSavingProfile(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { current_password: currentPw1 };
      if (usernameChanged) payload.new_username = newUsername.trim();
      if (newPassword) payload.new_password = newPassword;
      const res = await api.put('/auth/profile', payload, { headers: { Authorization: `Bearer ${token}` } });
      // Actualizar token (si cambió el username, el sub del JWT cambia)
      if (res.data.access_token) {
        localStorage.setItem('token', res.data.access_token);
      }
      toast.success('Datos actualizados correctamente');
      setCurrentPw1(''); setNewPassword(''); setConfirmPassword('');
      fetchProfile();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveSecurity = async (e) => {
    e.preventDefault();
    if (!currentPw2) { toast.error('Ingresa tu contraseña actual'); return; }
    if (!securityQuestion.trim() || !securityAnswer.trim()) { toast.error('Pregunta y respuesta son obligatorias'); return; }

    setSavingSecurity(true);
    try {
      const token = localStorage.getItem('token');
      await api.put('/auth/security-question',
        { current_password: currentPw2, question: securityQuestion.trim(), answer: securityAnswer.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Pregunta de seguridad actualizada');
      setCurrentPw2(''); setSecurityAnswer('');
      fetchProfile();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar la pregunta');
    } finally {
      setSavingSecurity(false);
    }
  };

  if (loading) {
    return <div className="text-white/60 text-sm uppercase tracking-wider">Cargando perfil...</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="account-panel">
      <div>
        <h2 className="text-xl md:text-2xl font-semibold text-white mb-1">Mi Cuenta</h2>
        <p className="text-xs text-white/50">Gestiona tu usuario, contraseña y pregunta de recuperación</p>
      </div>

      {/* Datos actuales */}
      <div className="bg-gradient-to-br from-purple-950/30 via-[#0A0A0A] to-emerald-950/20 border border-white/10 rounded-lg p-5">
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Sesión actual</p>
        <p className="text-lg font-semibold text-white">{profile?.username}</p>
        <p className="text-xs text-white/50 mt-1">{profile?.email}</p>
        <p className="text-[11px] text-white/40 mt-3">
          Pregunta de seguridad: {profile?.has_security_question
            ? <span className="text-emerald-400">configurada ✓</span>
            : <span className="text-amber-400">no configurada ⚠</span>}
        </p>
      </div>

      {/* Cambiar usuario / contraseña */}
      <Section testid="section-credentials" icon={Lock} title="Cambiar Usuario o Contraseña"
        description="Para cambiar tu nombre de usuario o contraseña, ingresa primero tu contraseña actual.">
        <form onSubmit={handleSaveProfile} className="space-y-4">
          <Field testid="profile-current-password" label="Contraseña actual" type="password"
            value={currentPw1} onChange={e => setCurrentPw1(e.target.value)} placeholder="••••••••" />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field testid="profile-new-username" label="Nuevo usuario" value={newUsername}
              onChange={e => setNewUsername(e.target.value)} placeholder={profile?.username} />
            <div className="hidden sm:block" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field testid="profile-new-password" label="Nueva contraseña" type="password"
              value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Mínimo 6 caracteres" />
            <Field testid="profile-confirm-password" label="Confirmar nueva contraseña" type="password"
              value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Repite la nueva contraseña" />
          </div>

          <div className="pt-2">
            <button
              data-testid="save-profile-btn"
              type="submit"
              disabled={savingProfile}
              className="px-5 py-2.5 bg-white text-black text-xs uppercase tracking-wider rounded-md hover:bg-white/90 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {savingProfile ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </Section>

      {/* Pregunta de seguridad */}
      <Section testid="section-security" icon={Shield}
        title="Pregunta de Seguridad"
        description="Esta pregunta se usará para recuperar tu contraseña si la olvidas. La respuesta no distingue mayúsculas ni espacios.">
        <form onSubmit={handleSaveSecurity} className="space-y-4">
          <Field testid="security-current-password" label="Contraseña actual" type="password"
            value={currentPw2} onChange={e => setCurrentPw2(e.target.value)} placeholder="••••••••" />

          <Field testid="security-question" label="Pregunta" value={securityQuestion}
            onChange={e => setSecurityQuestion(e.target.value)}
            placeholder="Ej: ¿Cómo se llama mi primera mascota?" />

          <Field testid="security-answer" label="Respuesta" type="password"
            value={securityAnswer} onChange={e => setSecurityAnswer(e.target.value)}
            placeholder="Tu respuesta secreta" />

          <p className="text-[11px] text-white/40">
            💡 Por seguridad, la respuesta nunca se muestra en pantalla. Si necesitas cambiarla, simplemente escribe una nueva.
          </p>

          <div className="pt-2">
            <button
              data-testid="save-security-btn"
              type="submit"
              disabled={savingSecurity}
              className="px-5 py-2.5 bg-white text-black text-xs uppercase tracking-wider rounded-md hover:bg-white/90 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              <Shield className="w-4 h-4" />
              {savingSecurity ? 'Guardando...' : 'Actualizar pregunta'}
            </button>
          </div>
        </form>
      </Section>
    </div>
  );
};

export default AccountPanel;
